import { uuidv4 } from '@/app/utils/uuid';

export const IDEMPOTENCY_HEADER = 'Idempotency-Key';

export function createIdempotencyKey() {
  return uuidv4();
}

/**
 * Hands out request keys derived from one operation key, so that retrying the operation
 * replays every POST it makes with the same keys. Requests to the same path get an ordinal,
 * otherwise sequential uploads (e.g. brand images) would collide on a single key.
 */
export class IdempotencyScope {
  constructor(key) {
    this.key = key;
    this._counters = new Map();
  }

  keyFor(url) {
    const path = getUrlPath(url);
    const ordinal = (this._counters.get(path) || 0) + 1;
    this._counters.set(path, ordinal);
    return `${this.key}:${path}:${ordinal}`;
  }
}

function getUrlPath(url) {
  try {
    return new URL(url).pathname;
  } catch (e) {
    return String(url);
  }
}

/**
 * A failed request may still have been applied when no response came back (dropped connection,
 * timeout) or the server failed (5xx). A 4xx means the backend refused it, so nothing was created.
 */
export function mayHaveBeenApplied(error) {
  const status = error?.response?.status;
  return !status || status >= 500;
}

/**
 * Remembers a key per operation id (e.g. an uploaded file) until the operation settles, so that
 * retrying it after a dropped connection reuses the key and the backend replays the result.
 */
export class IdempotencyKeyStore {
  constructor() {
    this._keys = new Map();
  }

  get(id) {
    if (!this._keys.has(id)) {
      this._keys.set(id, createIdempotencyKey());
    }
    return this._keys.get(id);
  }

  settle(id, error = null) {
    if (!error || !mayHaveBeenApplied(error)) {
      this._keys.delete(id);
    }
  }
}

/**
 * Identifies an upload across retries: picking the same file again yields a new File object,
 * so it is matched by name, size and modification time; URLs are matched as is.
 */
export function getUploadId(value) {
  if (value && typeof value === 'object' && 'size' in value) {
    return `file:${value.name}:${value.size}:${value.lastModified}`;
  }
  return String(value);
}

export function isSameOrigin(url, baseUrl) {
  try {
    return new URL(url).origin === new URL(baseUrl).origin;
  } catch (e) {
    return false;
  }
}
